import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  AlertTitle,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Upload,
  FilePresent,
  CheckCircle,
  // AlertCircle,
  ExpandMore,
  ExpandLess,
  Preview,
  Send,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';

interface ParsedQuestion {
  question: string;
  options: {
    optionA: string;
    optionB: string;
    optionC: string;
    optionD: string;
  };
  correctAnswer: string;
  category: string;
  difficulty: string;
  tags: string[];
  explanation: string;
}

interface ExcelImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (questions: ParsedQuestion[]) => Promise<void>;
  assessmentId: string;
  categories: any[];
}

export const ExcelImportDialog: React.FC<ExcelImportDialogProps> = ({
  open,
  onClose,
  onImport,
  categories,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [expandedPreview, setExpandedPreview] = useState(false);

  const steps = ['Upload Excel File', 'Preview Data', 'Import Questions'];

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setError('');
      setActiveStep(0);
      setParsedData([]);
    }
  };

  const parseExcelFile = async () => {
    if (!file) {
      setError('Please select an Excel file first');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, {
        cellStyles: true,
        // cellFormulas: true,
        cellDates: true,
      });

      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      // Convert to JSON with headers
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        setError('The Excel file appears to be empty');
        return;
      }

      // Validate required columns
      const requiredColumns = ['Question', 'OptionA', 'OptionB', 'OptionC', 'OptionD', 'Correct Option', 'Difficulty', 'Category', 'Explanation'];
      const sampleRow = jsonData[0] as any;
      const missingColumns = requiredColumns.filter(col => !(col in sampleRow));
      
      if (missingColumns.length > 0) {
        setError(`Missing required columns: ${missingColumns.join(', ')}`);
        return;
      }

      // Parse the data into the required format
      const formattedQuestions: ParsedQuestion[] = jsonData.map((row: any, index: number) => {
        // Validate required fields
        if (!row.Question || !row.Question.trim()) {
          throw new Error(`Row ${index + 2}: Question is required`);
        }
        console.log(row.Explanation)
        if (!row.Explanation || !row.Explanation?.toString()?.trim()) {
          throw new Error(`Row ${index + 2}: Explanation is required`);
        }

        // Generate tags from category and difficulty
        const tags: string[] = [];
        if (row.Category && row.Category.trim()) {
          tags.push(row.Category.toLowerCase().trim());
        }
        if (row.Difficulty && row.Difficulty.trim()) {
          tags.push(row.Difficulty.toLowerCase().trim());
        }

        // Find matching category ID
        const defaultCategoryId = categories?.[0]?._id || "60f7b1234567890abcdef123";
        const matchingCategory = categories?.find(
          cat => cat.name.toLowerCase() === (row.Category || '').toLowerCase()
        );

        return {
          question: row.Question.trim(),
          options: {
            optionA: row.OptionA?.toString()?.trim() || '',
            optionB: row.OptionB?.toString()?.trim() || '',
            optionC: row.OptionC?.toString()?.trim() || '',
            optionD: row.OptionD?.toString()?.trim() || '',
          },
          correctAnswer: row['Correct Option']?.toString()?.trim() || 'A',
          category: matchingCategory?._id || defaultCategoryId,
          difficulty: row.Difficulty?.toString()?.trim() || 'Beginner',
          tags: tags,
          explanation: row.Explanation?.toString()?.trim(),
        };
      });

      setParsedData(formattedQuestions);
      setActiveStep(1);
    } catch (err: any) {
      setError(`Error parsing file: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      setError('No data to import');
      return;
    }

    setIsImporting(true);
    try {
      await onImport(parsedData);
      setActiveStep(2);
    } catch (err: any) {
      setError(`Import failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setFile(null);
    setParsedData([]);
    setError('');
    setExpandedPreview(false);
    onClose();
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <FilePresent />
          <Typography variant="h6">Import Questions from Excel</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Expected Format Info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Expected Excel Format</AlertTitle>
          <Typography variant="body2">
            <strong>Required Columns:</strong> Question, OptionA, OptionB, OptionC, OptionD, Correct Option, Difficulty, Category, Explanation
          </Typography>
          <Typography variant="body2">
            <strong>Optional:</strong> SNo
          </Typography>
          <Typography variant="body2">
            <strong>Correct Option:</strong> Should be A, B, C, or D
          </Typography>
          <Typography variant="body2">
            <strong>Difficulty:</strong> Beginner, Intermediate, or Advanced
          </Typography>
        </Alert>

        {/* Step 0: File Upload */}
        {activeStep === 0 && (
          <Box>
            <Paper
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                mb: 3,
              }}
            >
              <FilePresent sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Upload Excel File
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Supports .xlsx and .xls files
              </Typography>
              <Button
                variant="contained"
                component="label"
                startIcon={<Upload />}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                />
              </Button>
              {file && (
                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </Paper>

            {file && (
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  onClick={parseExcelFile}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} /> : <Preview />}
                >
                  {isLoading ? 'Parsing...' : 'Parse Excel File'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Step 1: Preview Data */}
        {activeStep === 1 && parsedData.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Parsed Data Preview ({parsedData.length} questions)
              </Typography>
              <IconButton onClick={() => setExpandedPreview(!expandedPreview)}>
                {expandedPreview ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            {/* Sample Question Preview */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sample Question:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Q:</strong> {parsedData[0]?.question}
              </Typography>
              
              {/* Options with values */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Options:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    <strong>A:</strong> {parsedData[0]?.options.optionA}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    <strong>B:</strong> {parsedData[0]?.options.optionB}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    <strong>C:</strong> {parsedData[0]?.options.optionC}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    <strong>D:</strong> {parsedData[0]?.options.optionD}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={`Correct: ${parsedData[0]?.correctAnswer}`} 
                  color="primary" 
                  size="small" 
                />
                <Chip 
                  label={parsedData[0]?.difficulty} 
                  color={getDifficultyColor(parsedData[0]?.difficulty) as any}
                  size="small" 
                />
                <Chip 
                  label={`Category ID: ${parsedData[0]?.category}`} 
                  color="secondary" 
                  size="small" 
                />
              </Box>
              
              {parsedData[0]?.explanation && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
                  <strong>Explanation:</strong> {parsedData[0].explanation}
                </Typography>
              )}
              
              {/* Tags */}
              {parsedData[0]?.tags && parsedData[0].tags.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Tags:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {parsedData[0].tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Full Table Preview */}
            <Collapse in={expandedPreview}>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Question</TableCell>
                      <TableCell>A</TableCell>
                      <TableCell>B</TableCell>
                      <TableCell>C</TableCell>
                      <TableCell>D</TableCell>
                      <TableCell>Correct</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Difficulty</TableCell>
                      <TableCell>Explanation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData.map((question, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ maxWidth: 250 }}>
                          <Typography variant="body2" noWrap title={question.question}>
                            {question.question}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Typography variant="body2" noWrap title={question.options.optionA}>
                            {question.options.optionA}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Typography variant="body2" noWrap title={question.options.optionB}>
                            {question.options.optionB}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Typography variant="body2" noWrap title={question.options.optionC}>
                            {question.options.optionC}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 150 }}>
                          <Typography variant="body2" noWrap title={question.options.optionD}>
                            {question.options.optionD}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={question.correctAnswer} 
                            color="primary" 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 120 }}>
                          <Typography variant="body2" noWrap title={question.category}>
                            {question.category}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={question.difficulty} 
                            color={getDifficultyColor(question.difficulty) as any}
                            size="small" 
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={question.explanation}>
                            {question.explanation || 'No explanation'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>

            {/* JSON Preview Section */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">
                  Parsed JSON Preview (First 3 Questions)
                </Typography>
              </Box>
              
              <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
                <pre style={{ 
                  margin: 0, 
                  fontSize: '0.75rem', 
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify(parsedData.slice(0, 3), null, 2)}
                </pre>
              </Paper>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={isImporting}
                startIcon={isImporting ? <CircularProgress size={20} /> : <Send />}
                size="large"
              >
                {isImporting ? 'Importing...' : `Import ${parsedData.length} Questions`}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Success */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Import Successful!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {parsedData.length} questions have been imported successfully.
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {activeStep === 2 ? 'Close' : 'Cancel'}
        </Button>
        {activeStep === 1 && parsedData.length > 0 && (
          <Button onClick={() => setActiveStep(0)} variant="outlined">
            Back
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};